import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { Button } from "antd";
import {
  CloseOutlined,
  ExpandOutlined,
  CompressOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import "../style/PreviewModal.css";

Modal.setAppElement("#root");

export interface ButtonConfig {
  label: string | React.ReactNode;
  onClick: () => void;
  type?: "primary" | "default" | "link" | "dashed" | "text"; // remove "danger"
  danger?: boolean; // optional danger flag
  visible?: boolean;
}

export interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  title?: string | React.ReactNode;
  children: React.ReactNode;

  showMaximize?: boolean;
  showMinimize?: boolean;

  onMaximize?: () => void;
  onMinimize?: () => void;

  headerButtons?: ButtonConfig[];
  footerButtons?: ButtonConfig[];

  portalTarget?: HTMLElement | null;  
  draggable?: boolean;    // toggle draggable
}

export default function PreviewModal({
  open,
  onClose,
  title = "",
  children,
  showMaximize = true,
  showMinimize = true,
  onMaximize,
  onMinimize,
  headerButtons = [],
  footerButtons = [],
  portalTarget = null,
  draggable = true
}: PreviewModalProps) {
  const [visible, setVisible] = useState(open);
  const [isMaximized, setIsMaximized] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  /* ---------------- OPEN/CLOSE ---------------- */
  useEffect(() => {
    if (open) setVisible(true);
  }, [open]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  /* ---------------- MAXIMIZE ---------------- */
  const handleMaximize = () => {
    setIsMaximized(!isMaximized);
    onMaximize?.();
  };

  /* ---------------- DRAG ---------------- */
  useEffect(() => {
    if (!draggable || isMaximized) return;

    const modal = modalRef.current;
    if (!modal) return;

    let isDragging = false;
    let offsetX = 0;
    let offsetY = 0;

    const startDrag = (e: MouseEvent) => {
      const header = (e.target as HTMLElement).closest(".modal-header");
      if (!header || isMaximized) return; // disable drag in maximized state

      isDragging = true;
      offsetX = e.clientX - modal.offsetLeft;
      offsetY = e.clientY - modal.offsetTop;
      modal.style.transition = "none";
    };

    const drag = (e: MouseEvent) => {
      if (!isDragging) return;

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = modal.offsetWidth;
      const height = modal.offsetHeight;

      let x = e.clientX - offsetX;
      let y = e.clientY - offsetY;

      x = Math.max(0, Math.min(vw - width, x));
      y = Math.max(0, Math.min(vh - height, y));

      modal.style.left = `${x}px`;
      modal.style.top = `${y}px`;
    };

    const stopDrag = () => {
      isDragging = false;
      modal.style.transition = "";
    };

    document.addEventListener("mousedown", startDrag);
    document.addEventListener("mousemove", drag);
    document.addEventListener("mouseup", stopDrag);

    return () => {
      document.removeEventListener("mousedown", startDrag);
      document.removeEventListener("mousemove", drag);
      document.removeEventListener("mouseup", stopDrag);
    };
  }, [draggable, isMaximized]);

  return (
    <Modal
      isOpen={visible}
      onRequestClose={handleClose}
      overlayClassName="modal-overlay"
      className="modal-wrapper"
      closeTimeoutMS={250}
      parentSelector={() => portalTarget || document.body}
    >
      <div
        ref={modalRef}
        className={`modal-content ${visible ? "slide-in" : "slide-out"}`}
        style={
          isMaximized
            ? { width: "100vw", height: "100vh", top: 0, left: 0 }
            : {}
        }
      >
        {/* HEADER */}
        <div className="modal-header small-header">
          <h3 className="modal-title">{title}</h3>
          <div className="modal-actions">
            {headerButtons.map(
              ({ label, onClick, visible = true, type }, i) =>
                visible && (
                  <Button
                    key={i}
                    type={type || "default"}
                    size="small"
                    onClick={onClick}
                  >
                    {label}
                  </Button>
                )
            )}

            {showMinimize && (
              <Button
                type="text"
                icon={<MinusOutlined style={{ fontSize: 16 }} />}
                onClick={onMinimize}
              />
            )}

            {showMaximize && (
              <Button
                type="text"
                icon={
                  isMaximized ? (
                    <CompressOutlined style={{ fontSize: 16 }} />
                  ) : (
                    <ExpandOutlined style={{ fontSize: 16 }} />
                  )
                }
                onClick={handleMaximize}
              />
            )}

            <Button
              type="text"
              icon={<CloseOutlined style={{ fontSize: 16, color: "#ff4d4d" }} />}
              onClick={handleClose}
            />
          </div>
        </div>

        {/* BODY */}
        <div className="modal-body">{children}</div>

        {/* FOOTER */}
        {footerButtons.length > 0 && (
          <div className="modal-footer">
            {footerButtons.map(
              ({ label, onClick, visible = true, type }, i) =>
                visible && (
                  <Button
                    key={i}
                    type={type || "default"}
                    onClick={onClick}
                  >
                    {label}
                  </Button>
                )
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
